import { Flex, HStack, Spinner, TagLabel, Text } from "@chakra-ui/react";
import { useFormContext, useWatch } from "react-hook-form";

import { FeatureAutocompleteSelect } from "@/common/components/Autocomplete/FeatureAutocompleteSelect";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import RuleMetric, {
  featureMetricOptions,
} from "@/common/components/Rule/RuleMetric";
import RuleOperator from "@/common/components/Rule/RuleOperator";
import RuleValue, { FieldType } from "@/common/components/Rule/RuleValue";
import { useAttributeValues } from "@/common/data/useAttributeValues";
import { useFormFieldDiff } from "@/common/hooks/useFormFieldDiff";
import { useFormFieldUpdate } from "@/common/hooks/useFormFieldUpdate";
import { FeatureDisplay } from "@/feature/components/FeatureDisplay";
import { useFeatureNamesData } from "@/feature/data/useFeatureNamesData";
import {
  convertDateValueOperator,
  defaultTypeValues,
  isDayOperator,
  listOperators,
  operatorDisplayName,
  operatorsWithoutValue,
  relevantOperators,
  TypeByOperator,
} from "@reflag/shared/attributeFilter";
import {
  CompanyFeatureMetricFilter,
  FeatureMetric,
  featureMetricOperators,
} from "@reflag/shared/filter";

import { TextWordBreak } from "../TextWordBreak";
import FilterValue from "./FilterValue";

const defaultMetric: FeatureMetric = "funnelStep";

const dateMetrics: FeatureMetric[] = [
  "firstUsed",
  "lastUsed",
  "firstExposureAt",
  "lastExposureAt",
];

const getRelevantOperators = (metric: CompanyFeatureMetricFilter["metric"]) => {
  const operators = relevantOperators(["date", "any"]);

  const unfilteredOperators = dateMetrics.includes(metric)
    ? relevantOperators(["date", "any"])
    : ["funnelStep", "frequency", "satisfaction"].includes(metric)
      ? relevantOperators(["number", "list", "any"])
      : relevantOperators(["number", "any"]);

  for (const type in unfilteredOperators) {
    const t = type as keyof typeof unfilteredOperators;
    operators[t] = unfilteredOperators[t]!.filter((op) =>
      featureMetricOperators[metric].includes(op),
    );
  }

  return operators;
};

const getValueType = (
  metric: CompanyFeatureMetricFilter["metric"],
  operator: CompanyFeatureMetricFilter["operator"],
): FieldType => {
  if (operatorsWithoutValue.includes(operator)) {
    return "none";
  }

  if (listOperators.includes(operator)) {
    return "multiSelect";
  }

  switch (metric) {
    case "firstUsed":
    case "lastUsed":
    case "firstExposureAt":
    case "lastExposureAt":
      return "date";
    case "eventCount":
    case "exposureCount":
    case "usageCount":
      return "number";
    default:
      return "select";
  }
};

export function CompanyFeatureMetricFilterFields() {
  const form = useFormContext<CompanyFeatureMetricFilter>();
  const metric = useWatch({
    name: "metric",
    defaultValue: form.getValues("metric") ?? defaultMetric,
  });
  const operator = useWatch({ name: "operator" });
  const operators = getRelevantOperators(metric);
  const valueType = getValueType(metric, operator);

  // The field changed. Reset the operator to default if the field type does not support the current operator.
  useFormFieldUpdate(form, "metric", (newValue) => {
    if (form.getValues("type") !== "featureMetric") return;
    const currentOperator = form.getValues("operator");
    const operators = getRelevantOperators(newValue);
    const allowed = Object.values(operators).flat();

    if (!allowed.includes(currentOperator)) {
      form.setValue("operator", allowed[0], {
        shouldDirty: true,
      });
    }
    form.setValue("values", [""], {
      shouldDirty: true,
    });
  });

  // The operator changed. Reset value to default if the value type also changed.
  useFormFieldDiff(
    form,
    "operator",
    ({ oldValue: oldOperator, newValue: newOperator }) => {
      if (form.getValues("type") !== "featureMetric") return;
      const valueType = TypeByOperator[newOperator];
      if (valueType === "segment" || valueType === "featureTargeting") return;
      const previousValueType = oldOperator && TypeByOperator[oldOperator];
      if (valueType !== previousValueType) {
        form.setValue("values", defaultTypeValues(valueType, newOperator), {
          shouldDirty: true,
        });
      } else if (isDayOperator(oldOperator) !== isDayOperator(newOperator)) {
        const currentValue = form.getValues("values");
        const newValues = convertDateValueOperator(
          oldOperator,
          newOperator,
          currentValue ?? [""],
        );
        form.setValue("values", newValues, {
          shouldDirty: true,
        });
      } else {
        // Validate value field, since the new operator might not work
        // form the existing value
        form.trigger("values");
      }
    },
  );

  return (
    <>
      <ManagedFormControl
        name="featureId"
        render={({ field }) => (
          <FeatureAutocompleteSelect {...field} valueAsId />
        )}
      />

      <ManagedFormControl
        defaultValue={defaultMetric}
        name="metric"
        render={({ field }) => <RuleMetric {...field} />}
      />

      <ManagedFormControl
        name="operator"
        render={({ field }) => (
          <RuleOperator {...field} operators={operators} />
        )}
      />

      <ManagedFormControl
        key={`${metric}.${operator}`}
        name={listOperators.includes(operator) ? "values" : "values[0]"}
        render={({ field }) => (
          <RuleValue
            {...field}
            entityType={
              metric === "frequency"
                ? "feature-frequency"
                : metric === "satisfaction"
                  ? "feature-satisfaction"
                  : metric === "funnelStep"
                    ? "feature-funnelStep"
                    : "company"
            }
            fieldType={valueType}
            operator={operator}
          />
        )}
      />
    </>
  );
}

type CompanyFeatureMetricFilterItemProps = {
  filter: CompanyFeatureMetricFilter;
  noWrap?: boolean;
};

export function CompanyFeatureMetricFilterItem({
  filter,
  noWrap = false,
}: CompanyFeatureMetricFilterItemProps) {
  const { data: features = [], isLoading } = useFeatureNamesData();
  const feature = features?.find(({ id }) => id === filter.featureId);

  const metric = featureMetricOptions.find(
    ({ metric }) => metric === filter.metric,
  );
  const { data = [] } = useAttributeValues({
    entityType:
      filter.metric === "frequency"
        ? "feature-frequency"
        : filter.metric === "satisfaction"
          ? "feature-satisfaction"
          : filter.metric === "funnelStep"
            ? "feature-funnelStep"
            : "company",
  });

  const values = data.filter((v) => filter.values.includes(v.value));
  const type = dateMetrics.includes(filter.metric) ? "date" : "number";

  return (
    <TagLabel noOfLines={4} whiteSpace="normal">
      {isLoading ? (
        <Flex align="center" color="dimmed" columnGap={2}>
          <Spinner size="xs" variant="subtle" />
          <TextWordBreak as="span" noWrap={noWrap}>
            Loading filter...
          </TextWordBreak>
        </Flex>
      ) : (
        <Flex align="center" columnGap={1.5} wrap={noWrap ? "nowrap" : "wrap"}>
          {feature && (
            <FeatureDisplay as="span" feature={feature} maxW="36" size="sm" />
          )}
          {metric && (
            <TextWordBreak as="span" color="dimmed" noWrap={noWrap}>
              {metric.label}
            </TextWordBreak>
          )}
          <Text as="span" color="dimmed">
            {operatorDisplayName[filter.operator]}
          </Text>
          {values.length ? (
            <HStack spacing={2}>
              {values.map((value) => (
                <HStack key={value.value} spacing={1}>
                  {value.icon}
                  <Text as="span">{value.label}</Text>
                </HStack>
              ))}
            </HStack>
          ) : (
            <FilterValue
              operator={filter.operator}
              type={type}
              value={filter.values}
            />
          )}
        </Flex>
      )}
    </TagLabel>
  );
}
